import { useEffect, useRef } from "react";

/**
 * HeroDroplets — Canvas-2D layer of realistic floating water droplets
 * for the cinematic hero. Physics: gentle drift + occasional streak-down.
 *
 * Purpose: reinforce the brand story (water precision) without gimmicky 3D.
 * GPU-friendly: draws on offscreen canvas, ~40 droplets, 60fps easily.
 */
export default function HeroDroplets({ count = 32, opacity = 0.5 }) {
  const canvasRef = useRef(null);
  const dropsRef = useRef([]);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
    }
    resize();
    window.addEventListener("resize", resize);

    // Seed droplets — mixed sizes, gentle gravity, occasional streaks
    const rand = (a, b) => a + Math.random() * (b - a);
    dropsRef.current = new Array(count).fill(0).map(() => ({
      x: rand(0, 1),               // 0..1 normalized
      y: rand(0, 1),
      r: rand(2, 8),
      vy: rand(0.00006, 0.00028),  // very slow drift
      vx: rand(-0.00004, 0.00004),
      wobble: rand(0, Math.PI * 2),
      wobbleSpeed: rand(0.005, 0.02),
      opacity: rand(0.35, 0.9),
      streak: Math.random() > 0.85, // some droplets streak downward
    }));

    const ctx = canvas.getContext("2d");
    let last = performance.now();

    function draw(t) {
      const dt = Math.min(t - last, 60);
      last = t;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(dpr, dpr);

      for (const d of dropsRef.current) {
        d.wobble += d.wobbleSpeed;
        d.x += d.vx * dt + Math.sin(d.wobble) * 0.00006;
        d.y += d.vy * dt;

        // Recycle when off-screen
        if (d.y > 1.08) {
          d.y = -0.05;
          d.x = Math.random();
        }
        if (d.x > 1.05) d.x = -0.05;
        if (d.x < -0.05) d.x = 1.05;

        const px = d.x * W;
        const py = d.y * H;

        // Streak (elongated water trail)
        if (d.streak) {
          const grad = ctx.createLinearGradient(px, py - d.r * 12, px, py + d.r * 2);
          grad.addColorStop(0, "rgba(255,255,255,0)");
          grad.addColorStop(0.55, `rgba(255,255,255,${d.opacity * 0.14})`);
          grad.addColorStop(1, `rgba(255,255,255,${d.opacity * 0.32})`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.ellipse(px, py - d.r * 5, d.r * 0.5, d.r * 8, 0, 0, Math.PI * 2);
          ctx.fill();
        }

        // Droplet body — subtle radial highlight
        const rg = ctx.createRadialGradient(px - d.r * 0.4, py - d.r * 0.4, 0, px, py, d.r);
        rg.addColorStop(0, `rgba(255,255,255,${d.opacity * 0.9})`);
        rg.addColorStop(0.5, `rgba(232,217,197,${d.opacity * 0.4})`);
        rg.addColorStop(1, `rgba(198,168,124,${d.opacity * 0.1})`);
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(px, py, d.r, 0, Math.PI * 2);
        ctx.fill();

        // Tiny specular
        ctx.fillStyle = `rgba(255,255,255,${d.opacity * 0.75})`;
        ctx.beginPath();
        ctx.arc(px - d.r * 0.35, py - d.r * 0.35, d.r * 0.22, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(draw);
    }
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="hero-droplets"
      className="absolute inset-0 pointer-events-none"
      style={{ mixBlendMode: "screen", opacity }}
    />
  );
}
